import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { MAX_VIDEO_MEDIAFILE_SIZE, VIDEO_MEDIA_ACCEPT } from '@shared/config/media';
import { toast } from '@shared/ui';
import { useVideoMediafileUploads } from '@features/UploadVideoMediafiles';
import type { IVideoMediafile } from '../types';
import { TEXTS } from '../texts';
import { getPlayDuration } from './utils';
import type { SelectVideoMediafilesControllerProps } from './types';

export const useSelectVideoMediafilesController = ({
  companyId,
  query,
  refetch,
  videoMediafiles,
  hasPrevMediafiles,
}: SelectVideoMediafilesControllerProps) => {
  const [selectedItems, setSelectedItems] = useState<IVideoMediafile[]>([]);
  const uploads = useVideoMediafileUploads({ companyId, query, refetch });

  useEffect(() => setSelectedItems([]), [companyId]);

  const onSelectedChange = (value: boolean, item: IVideoMediafile) => {
    if (!item.id || item.uploadState || item.uploading) return;
    setSelectedItems((current) => {
      if (!value) return current.filter((selected) => selected.id !== item.id);
      if (current.some((selected) => selected.id === item.id)) return current;
      return [...current, { ...item, playDuration: getPlayDuration(item) }];
    });
  };

  const { open: onClickUpload, getInputProps } = useDropzone({
    onDrop: uploads.upload,
    accept: VIDEO_MEDIA_ACCEPT,
    maxSize: MAX_VIDEO_MEDIAFILE_SIZE,
    minSize: 1,
    onDropRejected: () => toast.error(TEXTS.INVALID_UPLOAD_FILE),
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  return {
    onSelectedChange,
    selectedItems,
    onClickUpload,
    getInputProps,
    onRetry: uploads.retry,
    onRemoveUpload: uploads.remove,
    uploadLoading: uploads.uploadLoading,
    items: [...uploads.items, ...(videoMediafiles ?? [])],
    nextButtonTitle: hasPrevMediafiles ? TEXTS.NEXT : TEXTS.CREATE_VIDEO_STREAM_MODAL_SELECT_BUTTON,
  };
};
